import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, onSnapshot } from 'firebase/firestore';
import { db, auth } from '../../../firebase';
import { useInquiry } from '../../../hooks/useInquiry';
import { CustomerInquiry, InquiryStatus, InquiryPriority, AdminResponseData, MessageData } from '../../../types/inquiry';

const InquiryDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { getInquiry, respondToInquiry, addMessage, updateInquiry, markAsRead } = useInquiry();

  const [inquiry, setInquiry] = useState<CustomerInquiry | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [adminResponse, setAdminResponse] = useState('');
  const [newMessage, setNewMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isAddingMessage, setIsAddingMessage] = useState(false);
  const [downloadingFile, setDownloadingFile] = useState<string | null>(null);

  // 실시간 업데이트 상태
  const [realtimeEnabled, setRealtimeEnabled] = useState(true);
  const [lastUpdateTime, setLastUpdateTime] = useState<Date>(new Date());
  const [connectionStatus, setConnectionStatus] = useState<'connected' | 'connecting' | 'disconnected'>('connecting');
  const [newMessagesCount, setNewMessagesCount] = useState(0);
  const [prevMessagesCount, setPrevMessagesCount] = useState(0);

  // 현재 관리자 정보
  const currentAdmin = auth.currentUser;

  // Firestore 타임스탬프를 Date로 변환하는 헬퍼 함수
  const convertTimestampToDate = useCallback((data: any): CustomerInquiry => {
    return {
      ...data,
      createdAt: data.createdAt?.toDate() || new Date(),
      updatedAt: data.updatedAt?.toDate() || new Date(),
      adminResponse: data.adminResponse ? {
        ...data.adminResponse,
        respondedAt: data.adminResponse.respondedAt?.toDate() || new Date()
      } : undefined,
      messages: data.messages?.map((msg: any) => ({
        ...msg,
        createdAt: msg.createdAt?.toDate() || new Date()
      })) || [],
      lastReadBy: data.lastReadBy ? Object.keys(data.lastReadBy).reduce((acc, uid) => {
        acc[uid] = data.lastReadBy[uid]?.toDate() || new Date();
        return acc;
      }, {} as { [key: string]: Date }) : undefined
    };
  }, []);

  const loadInquiry = useCallback(async () => {
    if (!id) return;

    try {
      setLoading(true);
      setError(null);

      const inquiryData = await getInquiry(id);
      if (inquiryData) {
        setInquiry(inquiryData);
        setPrevMessagesCount(inquiryData.messages?.length || 0);

        // 문의 데이터 로드 후 읽음 처리 (관리자로서)
        if (currentAdmin) {
          try {
            await markAsRead(id);
          } catch (readError) {
            console.warn('읽음 상태 업데이트 실패:', readError);
          }
        }
      } else {
        setError('문의를 찾을 수 없습니다.');
      }
    } catch (err) {
      console.error('문의 로드 실패:', err);
      setError(err instanceof Error ? err.message : '문의를 불러오는데 실패했습니다.');
      setConnectionStatus('disconnected');
    } finally {
      setLoading(false);
    }
  }, [id, getInquiry, currentAdmin, markAsRead]);

  // 실시간 스트림 설정
  useEffect(() => {
    if (!id || !realtimeEnabled) {
      if (id) {
        loadInquiry();
      }
      return;
    }

    setConnectionStatus('connecting');

    const unsubscribe = onSnapshot(
      doc(db, 'customer_inquiries', id),
      (doc) => {
        if (doc.exists()) {
          const inquiryData = convertTimestampToDate({
            id: doc.id,
            ...doc.data()
          });

          // 새 메시지 카운트 업데이트
          const currentMessagesCount = inquiryData.messages?.length || 0;
          if (prevMessagesCount > 0 && currentMessagesCount > prevMessagesCount) {
            const newCount = currentMessagesCount - prevMessagesCount;
            setNewMessagesCount(prev => prev + newCount);
          }

          setInquiry(inquiryData);
          setPrevMessagesCount(currentMessagesCount);
          setConnectionStatus('connected');
          setLastUpdateTime(new Date());
          setLoading(false);
        } else {
          setError('문의를 찾을 수 없습니다.');
          setConnectionStatus('disconnected');
          setLoading(false);
        }
      },
      (error) => {
        console.error('실시간 업데이트 실패:', error);
        setError('실시간 업데이트에 실패했습니다.');
        setConnectionStatus('disconnected');
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [id, realtimeEnabled, convertTimestampToDate, prevMessagesCount]);

  // 초기 로드 (실시간이 비활성화된 경우)
  useEffect(() => {
    if (id && !realtimeEnabled) {
      loadInquiry();
    }
  }, [id, realtimeEnabled, loadInquiry]);


  // 새 메시지 알림 클리어
  const clearNewMessageNotification = useCallback(() => {
    setNewMessagesCount(0);
  }, []);

  // 실시간 토글
  const toggleRealtime = useCallback(() => {
    setRealtimeEnabled(!realtimeEnabled);
  }, [realtimeEnabled]);

  // 관리자 답변 제출
  const handleSubmitResponse = async () => {
    if (!inquiry || !adminResponse.trim() || !currentAdmin) return;

    try {
      setIsSubmitting(true);
      setError(null);

      // 관리자 프로필에서 이름 가져오기
      let adminName = '관리자';
      try {
        const adminProfileRef = doc(db, 'admin_profiles', currentAdmin.uid);
        const adminProfileSnap = await getDoc(adminProfileRef);

        if (adminProfileSnap.exists()) {
          const adminData = adminProfileSnap.data();
          adminName = adminData.name || currentAdmin.displayName || '관리자';
        } else {
          adminName = currentAdmin.displayName || '관리자';
        }
      } catch (profileError) {
        console.warn('관리자 프로필 조회 실패:', profileError);
        adminName = currentAdmin.displayName || '관리자';
      }

      const responseData: AdminResponseData = {
        content: adminResponse.trim(),
        adminUid: currentAdmin.uid,
        adminName: adminName
      };

      await respondToInquiry(inquiry.id, responseData);

      // 문의 다시 로드
      await loadInquiry();
      setAdminResponse('');
    } catch (err) {
      console.error('답변 등록 실패:', err);
      setError(err instanceof Error ? err.message : '답변 등록에 실패했습니다.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // 추가 메시지 제출
  const handleAddMessage = async () => {
    if (!inquiry || !newMessage.trim() || !currentAdmin) return;

    try {
      setIsAddingMessage(true);
      setError(null);

      // 관리자 프로필에서 이름 가져오기
      let adminName = '관리자';
      try {
        const adminProfileRef = doc(db, 'admin_profiles', currentAdmin.uid);
        const adminProfileSnap = await getDoc(adminProfileRef);

        if (adminProfileSnap.exists()) {
          const adminData = adminProfileSnap.data();
          adminName = adminData.name || currentAdmin.displayName || '관리자';
        } else {
          adminName = currentAdmin.displayName || '관리자';
        }
      } catch (profileError) {
        console.warn('관리자 프로필 조회 실패:', profileError);
        adminName = currentAdmin.displayName || '관리자';
      }

      const messageData: MessageData = {
        content: newMessage.trim(),
        senderUid: currentAdmin.uid,
        senderType: 'admin',
        senderName: adminName
      };

      await addMessage(inquiry.id, messageData);

      // 문의 다시 로드
      await loadInquiry();
      setNewMessage('');
    } catch (err) {
      console.error('메시지 추가 실패:', err);
      setError(err instanceof Error ? err.message : '메시지 추가에 실패했습니다.');
    } finally {
      setIsAddingMessage(false);
    }
  };

  // 문의 상태 변경
  const handleStatusChange = async (newStatus: InquiryStatus) => {
    if (!inquiry) return;

    try {
      setError(null);
      await updateInquiry(inquiry.id, { status: newStatus });
      await loadInquiry();
    } catch (err) {
      console.error('상태 변경 실패:', err);
      setError(err instanceof Error ? err.message : '상태 변경에 실패했습니다.');
    }
  };

  // 우선순위 변경
  const handlePriorityChange = async (newPriority: InquiryPriority) => {
    if (!inquiry) return;

    try {
      setError(null);
      await updateInquiry(inquiry.id, { priority: newPriority });
      await loadInquiry();
    } catch (err) {
      console.error('우선순위 변경 실패:', err);
      setError(err instanceof Error ? err.message : '우선순위 변경에 실패했습니다.');
    }
  };

  // Firebase Storage URL에서 실제 파일명 추출 및 디코딩
  const extractFileName = (url: string): string => {
    try {
      const urlObj = new URL(url);
      const pathParam = urlObj.searchParams.get('name');

      if (pathParam) {
        const decodedPath = decodeURIComponent(pathParam);
        const match = decodedPath.match(/\d+_(.+)$/);
        if (match) {
          return match[1];
        }
        return decodedPath.split('/').pop() || '첨부파일';
      }

      const pathParts = url.split('/').pop()?.split('?')[0];
      if (pathParts) {
        const decoded = decodeURIComponent(pathParts);
        const match = decoded.match(/\d+_(.+)$/);
        if (match) {
          return match[1];
        }
        return decoded.split('/').pop() || '첨부파일';
      }

      return '첨부파일';
    } catch (error) {
      console.error('파일명 추출 실패:', error);
      return '첨부파일';
    }
  };

  // 첨부파일 다운로드 (보안 개선: URL 노출 방지)
  const handleDownloadAttachment = async (url: string, filename: string) => {
    try {
      setDownloadingFile(filename);
      setError(null);

      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = filename;
      link.style.display = 'none';

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      URL.revokeObjectURL(objectUrl);
    } catch (error) {
      console.error('파일 다운로드 실패:', error);
      setError('파일 다운로드에 실패했습니다. 잠시 후 다시 시도해주세요.');
    } finally {
      setDownloadingFile(null);
    }
  };

  // 라벨 변환 함수들
  const getStatusLabel = (status: InquiryStatus): string => {
    switch (status) {
      case 'pending': return '답변대기';
      case 'answered': return '답변완료';
      case 'closed': return '문의완료';
      default: return status;
    }
  };

  const getCategoryLabel = (category: string): string => {
    switch (category) {
      case 'general': return '일반문의';
      case 'portville': return '포트빌';
      case 'parking': return '주차장';
      case 'facility': return '시설물';
      case 'account': return '계정';
      default: return category;
    }
  };

  const getPriorityLabel = (priority: InquiryPriority): string => {
    switch (priority) {
      case 'high': return '긴급';
      case 'normal': return '보통';
      case 'low': return '낮음';
      default: return priority;
    }
  };

  // 색상 클래스 함수들
  const getStatusColor = (status: InquiryStatus): string => {
    switch (status) {
      case 'pending': return 'text-orange-600 bg-orange-50 border-orange-200';
      case 'answered': return 'text-green-600 bg-green-50 border-green-200';
      case 'closed': return 'text-gray-600 bg-gray-50 border-gray-200';
      default: return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const getPriorityColor = (priority: InquiryPriority): string => {
    switch (priority) {
      case 'high': return 'text-red-600 bg-red-50 border-red-200';
      case 'normal': return 'text-blue-600 bg-blue-50 border-blue-200';
      case 'low': return 'text-gray-600 bg-gray-50 border-gray-200';
      default: return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  // 날짜 포맷팅
  const formatDate = (date: Date): string => {
    return new Intl.DateTimeFormat('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
          <span className="ml-2 text-gray-600">문의를 불러오는 중...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="text-red-800 font-medium">오류가 발생했습니다</div>
          <div className="text-red-600 text-sm mt-1">{error}</div>
        </div>
      </div>
    );
  }

  if (!inquiry) {
    return (
      <div className="p-6">
        <div className="text-center py-12">
          <h3 className="text-lg font-medium text-gray-900 mb-2">문의를 찾을 수 없습니다</h3>
          <button
            onClick={() => navigate('/admin/inquiry')}
            className="text-indigo-600 hover:text-indigo-500"
          >
            목록으로 돌아가기
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      {/* 헤더 */}
      <div className="mb-6">
        <div className="flex items-center gap-2 text-sm text-gray-600 mb-4">
          <button
            onClick={() => navigate('/admin/inquiry')}
            className="text-indigo-600 hover:text-indigo-700"
          >
            고객센터 관리
          </button>
          <span>{'>'}</span>
          <span>문의 상세</span>
        </div>

        {/* 실시간 상태 표시 */}
        <div className="flex items-center justify-between mb-4 p-3 bg-gray-50 rounded-lg">
          <div className="flex items-center space-x-4">
            {/* 실시간 연결 상태 */}
            <div className="flex items-center">
              <div className={`w-2 h-2 rounded-full mr-2 ${
                connectionStatus === 'connected' ? 'bg-green-500 animate-pulse' :
                connectionStatus === 'connecting' ? 'bg-yellow-500 animate-pulse' :
                'bg-red-500'
              }`}></div>
              <span className={`text-xs font-medium ${
                connectionStatus === 'connected' ? 'text-green-700' :
                connectionStatus === 'connecting' ? 'text-yellow-700' :
                'text-red-700'
              }`}>
                {connectionStatus === 'connected' ? '실시간 연결' :
                 connectionStatus === 'connecting' ? '연결 중' :
                 '연결 끊어짐'}
              </span>
            </div>

            {/* 새 메시지 알림 */}
            {newMessagesCount > 0 && (
              <div className="flex items-center">
                <div className="bg-blue-100 border border-blue-200 rounded px-2 py-1 flex items-center">
                  <svg className="w-3 h-3 text-blue-600 mr-1 animate-bounce" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                  </svg>
                  <span className="text-blue-800 font-medium text-xs">새 메시지 {newMessagesCount}건</span>
                  <button
                    onClick={clearNewMessageNotification}
                    className="ml-1 text-blue-600 hover:text-blue-800"
                  >
                    ✕
                  </button>
                </div>
              </div>
            )}

            {/* 마지막 업데이트 시간 */}
            <div className="text-xs text-gray-500">
              업데이트: {lastUpdateTime.toLocaleTimeString()}
            </div>
          </div>

          {/* 실시간 토글 버튼 */}
          <button
            onClick={toggleRealtime}
            className={`p-1 rounded transition-colors text-xs ${
              realtimeEnabled ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
            }`}
            title={realtimeEnabled ? '실시간 업데이트 끄기' : '실시간 업데이트 켜기'}
          >
            {realtimeEnabled ? '실시간 ON' : '실시간 OFF'}
          </button>
        </div>

        <div className="flex items-start justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">{inquiry.subject}</h1>
            <div className="flex items-center gap-3">
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getPriorityColor(inquiry.priority)}`}>
                {getPriorityLabel(inquiry.priority)}
              </span>
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getStatusColor(inquiry.status)}`}>
                {getStatusLabel(inquiry.status)}
              </span>
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium text-gray-800 bg-gray-100">
                {getCategoryLabel(inquiry.category)}
              </span>
            </div>
          </div>

          {/* 상태 및 우선순위 변경 */}
          <div className="flex flex-col gap-2">
            <select
              value={inquiry.status}
              onChange={(e) => handleStatusChange(e.target.value as InquiryStatus)}
              className="text-sm border border-gray-300 rounded-md px-3 py-1"
            >
              <option value="pending">답변대기</option>
              <option value="answered">답변완료</option>
              <option value="closed">문의완료</option>
            </select>

            <select
              value={inquiry.priority}
              onChange={(e) => handlePriorityChange(e.target.value as InquiryPriority)}
              className="text-sm border border-gray-300 rounded-md px-3 py-1"
            >
              <option value="high">긴급</option>
              <option value="normal">보통</option>
              <option value="low">낮음</option>
            </select>
          </div>
        </div>
      </div>

      {/* 사용자 정보 */}
      <div className="bg-white shadow rounded-lg p-6 mb-6">
        <h2 className="text-lg font-medium text-gray-900 mb-4">문의자 정보</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <dt className="text-sm font-medium text-gray-500">이름</dt>
            <dd className="mt-1 text-sm text-gray-900">{inquiry.userInfo.name}</dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-gray-500">이메일</dt>
            <dd className="mt-1 text-sm text-gray-900">{inquiry.userInfo.email}</dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-gray-500">소속</dt>
            <dd className="mt-1 text-sm text-gray-900">{inquiry.userInfo.organization}</dd>
          </div>
        </div>
        <div className="mt-4">
          <dt className="text-sm font-medium text-gray-500">작성일시</dt>
          <dd className="mt-1 text-sm text-gray-900">{formatDate(inquiry.createdAt)}</dd>
        </div>
      </div>

      {/* 문의 내용 */}
      <div className="bg-white shadow rounded-lg p-6 mb-6">
        <h2 className="text-lg font-medium text-gray-900 mb-4">문의 내용</h2>
        <div className="prose max-w-none">
          <div className="whitespace-pre-wrap text-gray-900">{inquiry.content}</div>
        </div>

        {/* 첨부파일 */}
        {inquiry.attachments && inquiry.attachments.length > 0 && (
          <div className="mt-6">
            <h3 className="text-sm font-medium text-gray-700 mb-3">첨부파일</h3>
            <div className="space-y-2">
              {inquiry.attachments.map((url, index) => {
                const filename = extractFileName(url);
                return (
                  <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded border">
                    <div className="flex items-center">
                      <svg className="w-5 h-5 text-gray-400 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                      </svg>
                      <span className="text-gray-700">{filename}</span>
                    </div>
                    <button
                      onClick={() => handleDownloadAttachment(url, filename)}
                      disabled={downloadingFile === filename}
                      className="text-indigo-600 hover:text-indigo-700 text-sm font-medium disabled:text-gray-400 disabled:cursor-not-allowed flex items-center gap-1"
                    >
                      {downloadingFile === filename ? (
                        <>
                          <div className="animate-spin rounded-full h-3 w-3 border-b border-indigo-600"></div>
                          <span>다운로드 중...</span>
                        </>
                      ) : (
                        '다운로드'
                      )}
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* 관리자 답변 */}
      {inquiry.adminResponse ? (
        <div className="bg-white shadow rounded-lg p-6 mb-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">관리자 답변</h2>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-medium text-blue-900">
                {inquiry.adminResponse.adminName}
              </span>
              <span className="text-sm text-blue-700">
                {formatDate(inquiry.adminResponse.respondedAt)}
              </span>
            </div>
            <div className="prose max-w-none">
              <div className="whitespace-pre-wrap text-blue-900">{inquiry.adminResponse.content}</div>
            </div>
          </div>
        </div>
      ) : (
        <div className="bg-white shadow rounded-lg p-6 mb-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">관리자 답변 작성</h2>
          <div className="space-y-4">
            <textarea
              value={adminResponse}
              onChange={(e) => setAdminResponse(e.target.value)}
              rows={8}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
              placeholder="답변 내용을 입력하세요..."
            />
            <div className="flex justify-end">
              <button
                onClick={handleSubmitResponse}
                disabled={!adminResponse.trim() || isSubmitting}
                className="px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-md hover:bg-indigo-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                {isSubmitting ? '답변 등록 중...' : '답변 등록'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 추가 메시지 */}
      {inquiry.messages && inquiry.messages.length > 0 && (
        <div className="bg-white shadow rounded-lg p-6 mb-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">대화 내용</h2>
          <div className="space-y-4">
            {inquiry.messages.map((message, index) => {
              // 최근 메시지인지 확인 (5분 이내)
              const fiveMinutesAgo = new Date(Date.now() - 5 * 60 * 1000);
              const isRecentMessage = message.createdAt > fiveMinutesAgo;

              return (
                <div key={message.id} className={`flex ${message.senderType === 'admin' ? 'justify-end' : 'justify-start'} ${
                  isRecentMessage ? 'animate-fadeIn' : ''
                }`}>
                  <div className={`max-w-xs lg:max-w-md ${message.senderType === 'admin' ? 'order-1' : 'order-2'} relative`}>
                    {isRecentMessage && (
                      <div className="absolute -top-2 -left-2">
                        <span className="inline-flex items-center px-1 py-0.5 text-xs font-medium text-green-800 bg-green-100 rounded-full border border-green-200 animate-pulse">
                          NEW
                        </span>
                      </div>
                    )}
                    <div className={`p-3 rounded-lg ${
                      message.senderType === 'admin'
                        ? `bg-blue-600 text-white ${isRecentMessage ? 'ring-2 ring-blue-300' : ''}`
                        : `bg-gray-100 text-gray-900 ${isRecentMessage ? 'ring-2 ring-green-300' : ''}`
                    }`}>
                      <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                    </div>
                    <div className={`text-xs text-gray-500 mt-1 ${message.senderType === 'admin' ? 'text-right' : 'text-left'}`}>
                      <div className="flex items-center gap-1">
                        {formatDate(message.createdAt)}
                        {isRecentMessage && (
                          <svg className="w-3 h-3 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* 추가 메시지 작성 */}
      <div className="bg-white shadow rounded-lg p-6">
        <h2 className="text-lg font-medium text-gray-900 mb-4">추가 메시지</h2>
        <div className="space-y-4">
          <textarea
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            placeholder="추가로 전달하고 싶은 내용이 있으시면 작성해주세요..."
            rows={4}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
            maxLength={1000}
          />
          <div className="flex items-center justify-between">
            <span className="text-xs text-gray-500">{newMessage.length}/1000</span>
            <button
              onClick={handleAddMessage}
              disabled={isAddingMessage || !newMessage.trim()}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
            >
              {isAddingMessage ? (
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  전송 중...
                </div>
              ) : (
                '메시지 보내기'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InquiryDetailPage;